import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import SelecaoColaboradores, { selecaoColaboradoresVazia, SelecaoColaboradoresValor } from '../components/SelecaoColaboradores'

interface ResultadoItem {
  motcod: string
  motnome: string
  status: 'ok' | 'sem_ficha' | 'ficha_fechada' | 'erro'
  registrosEncontrados?: number
  camposAtualizados?: number
  camposIgnorados?: number
  diasNaoEncontrados?: string[]
  erro?: string
}

const LABEL_STATUS: Record<ResultadoItem['status'], string> = {
  ok: 'Importado', sem_ficha: 'Sem ficha nesse período', ficha_fechada: 'Ficha fechada', erro: 'Erro',
}

export default function ImportarBusrepLote() {
  const navigate = useNavigate()
  const [selecao, setSelecao] = useState<SelecaoColaboradoresValor>(selecaoColaboradoresVazia())
  const [ano, setAno] = useState(new Date().getFullYear())
  const [mes, setMes] = useState(new Date().getMonth() + 1)
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [sobrescrever, setSobrescrever] = useState(false)
  const [erro, setErro] = useState('')
  const [importando, setImportando] = useState(false)
  const [resultados, setResultados] = useState<ResultadoItem[] | null>(null)
  const [periodoMisto, setPeriodoMisto] = useState(false)

  // O período a consultar no BUSREP é sugerido a partir do parâmetro cadastrado pra
  // empresa e dissídio de cada colaborador (dia_inicio_periodo, "parâmetro 1") — o mesmo
  // palpite usado ao abrir fichas em lote. Recalcula sempre que a seleção, o ano ou o mês
  // mudam; o digitador ainda pode ajustar as datas manualmente antes de importar.
  useEffect(() => {
    if (selecao.modo === 'individual' && selecao.colaboradoresIndividuais.length === 0) return
    if (selecao.modo === 'categoria' && selecao.catcat.length === 0) return
    const params: Record<string, string | number> = { ano, mes, modo: selecao.modo }
    if (selecao.modo === 'categoria') params.catcat = selecao.catcat.join(',')
    if (selecao.modo === 'individual') params.colaboradores = selecao.colaboradoresIndividuais.map(c => c.motcod).join(',')
    if (selecao.excluidos.length > 0) params.excluidos = selecao.excluidos.join(',')
    api.get('/cartao-ponto/periodo-sugerido-lote', { params }).then(r => {
      setDataInicio(r.data.dataInicio)
      setDataFim(r.data.dataFim)
      setPeriodoMisto(Boolean(r.data.misto))
    })
  }, [selecao.modo, selecao.catcat, selecao.colaboradoresIndividuais, selecao.excluidos, ano, mes])

  async function importar() {
    if (!dataInicio || !dataFim) { setErro('Informe o período a consultar no BUSREP.'); return }
    if (selecao.modo === 'individual' && selecao.colaboradoresIndividuais.length === 0) { setErro('Selecione ao menos um colaborador.'); return }
    if (selecao.modo === 'categoria' && selecao.catcat.length === 0) { setErro('Selecione ao menos uma categoria de cargo.'); return }

    setImportando(true)
    setErro('')
    setResultados(null)
    try {
      const r = await api.post('/cartao-ponto/fichas/importar-busrep-lote', {
        modo: selecao.modo, catcat: selecao.catcat, colaboradores: selecao.colaboradoresIndividuais.map(c => c.motcod),
        excluidos: selecao.excluidos,
        ano, mes, dataInicio, dataFim, sobrescrever,
      })
      setResultados(r.data.resultados)
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao importar do BUSREP.')
    } finally {
      setImportando(false)
    }
  }

  return (
    <div style={{ maxWidth: 900 }}>
      <div className="card">
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '1rem' }}>Importar Registros do BUSREP em Lote</h3>
        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: -8, marginBottom: '1rem' }}>
          Importa nas fichas já abertas de ano/mês informados. Colaboradores sem ficha nesse período são ignorados.
        </p>

        <SelecaoColaboradores valor={selecao} onChange={setSelecao} />

        <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '1.2rem' }}>
          <div className="field">
            <label>Ano da ficha *</label>
            <input type="number" value={ano} onChange={e => setAno(Number(e.target.value))} />
          </div>
          <div className="field">
            <label>Mês da ficha *</label>
            <select value={mes} onChange={e => setMes(Number(e.target.value))}>
              {['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
                .map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Período a consultar no BUSREP — de *</label>
            <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
          </div>
          <div className="field">
            <label>Período a consultar no BUSREP — até *</label>
            <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
          </div>
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: '0.8rem', cursor: 'pointer', fontSize: '0.9rem' }}>
          <input type="checkbox" checked={sobrescrever} onChange={e => setSobrescrever(e.target.checked)} />
          Sobrescrever registros já importados/preenchidos
        </label>

        {periodoMisto && (
          <div style={{ color: '#b45309', fontSize: '0.8rem', marginTop: '0.5rem' }}>
            Os colaboradores selecionados têm períodos diferentes configurados — as datas acima seguem o período mais comum entre eles. Confira antes de importar.
          </div>
        )}

        {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.5rem' }}>{erro}</div>}

        {resultados && (
          <div style={{ marginTop: '1rem', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '4px 8px' }}>Colaborador</th>
                  <th style={{ padding: '4px 8px' }}>Situação</th>
                  <th style={{ padding: '4px 8px' }}>Registros</th>
                  <th style={{ padding: '4px 8px' }}>Atualizados</th>
                  <th style={{ padding: '4px 8px' }}>Ignorados</th>
                  <th style={{ padding: '4px 8px' }}>Dias não encontrados</th>
                </tr>
              </thead>
              <tbody>
                {resultados.map(r => (
                  <tr key={r.motcod} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '4px 8px' }}>{r.motcod} - {r.motnome}</td>
                    <td style={{ padding: '4px 8px', color: r.status === 'ok' ? '#15803d' : r.status === 'erro' ? '#b91c1c' : '#6b7280' }}>
                      {LABEL_STATUS[r.status]}{r.erro ? `: ${r.erro}` : ''}
                    </td>
                    <td style={{ padding: '4px 8px' }}>{r.registrosEncontrados ?? '—'}</td>
                    <td style={{ padding: '4px 8px' }}>{r.camposAtualizados ?? '—'}</td>
                    <td style={{ padding: '4px 8px' }}>{r.camposIgnorados ?? '—'}</td>
                    <td style={{ padding: '4px 8px' }}>{r.diasNaoEncontrados?.length ? r.diasNaoEncontrados.join(', ') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
          <button className="btn btn-primary" onClick={importar} disabled={importando}>
            {importando ? 'Importando...' : 'Importar'}
          </button>
          <button className="btn btn-outline" onClick={() => navigate('/funcionarios/ficha-ponto')}>Voltar</button>
        </div>
      </div>
    </div>
  )
}
